import { isValidPhoneNumber } from 'react-phone-number-input';

import { ContactProps, ContactType } from '@/models/Contact';
import { USCountryId } from './constants';
import { isEmail } from './emailUtil';

export const requiredPrimaryContactFieldsByProgram = (
  contactType?: ContactType
): Array<keyof ContactProps> => {
  const baseRequiredFields: Array<keyof ContactProps> = [
    'firstName',
    'lastName',
    'streetAddress1',
    'city',
    'countyId',
    'stateId',
    'zipCode',
  ];

  if (contactType?.displayPhone) baseRequiredFields.push('phone');
  if (contactType?.displayEmail) baseRequiredFields.push('email');
  if (contactType?.displayTitle) baseRequiredFields.push('title');
  if (contactType?.displayDepartment) {
    baseRequiredFields.push('departmentTypeId');
    baseRequiredFields.push('departmentOther');
  }
  if (contactType?.displaySubType) baseRequiredFields.push('contactSubTypeId');

  return baseRequiredFields;
};

export function phoneNumberIsValid(phone: string | null): boolean {
  if (phone == null) return false;

  let updatedPhone = phone.trim();

  // these functions require the + be on the front of the string, so we add it above
  if (updatedPhone != null && updatedPhone[0] !== '+') {
    updatedPhone = `+${updatedPhone}`;
  }

  if (!isValidPhoneNumber(updatedPhone)) {
    return false;
  }

  return true;
}

export function stateIdIsRequired(countryId: number) {
  return countryId === USCountryId;
}

export const countyIdIsRequired = (countryId: number) => {
  return stateIdIsRequired(countryId);
};

export const validateContact = (
  contact: ContactProps,
  contactType?: ContactType
): Array<keyof ContactProps> => {
  const emptyContact = {
    id: null,
    firstName: null,
    lastName: null,
    phone: null,
    email: null,
    consent: null,
    certification: null,
    departmentOther: null,
    contactSubTypeId: null,
    departmentTypeId: null,
    title: null,
    linkedIn: null,
    whatsApp: null,
    yearsExperience: null,
  } satisfies ContactProps;

  // TODO(bug): `emptyContact` omits the address keys (streetAddress1, city, countyId, stateId,
  // zipCode) even though they are always in `requiredFields`, so a contact object that simply lacks
  // those keys (e.g. a freshly built `{ contactRelationships: [] }`) passes address validation.
  // Expected: seed every required key here (or iterate `requiredFields` instead of Object.keys).
  // if we dont do this and the contact is an empty object ({}) then Object.keys will return an empty array which makes it look like no fields are missing
  contact = { ...emptyContact, ...contact };

  const requiredFields = requiredPrimaryContactFieldsByProgram(contactType);
  const invalidContactFields: Array<keyof ContactProps> = [];
  Object.keys(contact).forEach((key: string) => {
    const contactKey = key as keyof ContactProps;
    const value = contact[contactKey];
    if (value == null || value === '') {
      if (!requiredFields.includes(contactKey)) {
        return;
      } else if (
        key === 'stateId' &&
        contact.countryId != null &&
        !stateIdIsRequired(contact.countryId)
      ) {
        return;
      } else if (
        key === 'countyId' &&
        contact.countryId != null &&
        !countyIdIsRequired(contact.countryId)
      ) {
        return;
      } else if (key === 'departmentOther') {
        const selectedDepartment = contactType?.departmentTypes?.find((subType) => {
          return subType.id === contact.departmentTypeId;
        });

        if ((selectedDepartment?.name ?? '').toLowerCase() !== 'other') return;
      }

      invalidContactFields.push(contactKey);
    } else if (requiredFields.includes('phone') && key === 'phone') {
      if (!phoneNumberIsValid(value as string)) {
        invalidContactFields.push(contactKey);
      }
    } else if (requiredFields.includes('email') && key === 'email') {
      if (!isEmail(value as string)) {
        invalidContactFields.push(contactKey);
      }
    }
  });

  return invalidContactFields;
};
